<template>
  <div class="my-8">
    <div class="mt-10 sm:mt-0">
      <div class="md:grid md:grid-cols-3 md:gap-6">
        <div class="md:col-span-1">
          <div class="px-4 sm:px-0">
            <span class="text-lg font-medium leading-6 text-gray-900">{{ hub['gateway_eui'] }}</span>
            <div v-if="canSeeExtendedInfo"
                 class="col-span-6 sm:col-span-3">
              <label for="model-number" class="block text-sm font-medium text-gray-700">Hub Type</label>
              <input type="text" name="model-number" id="model-number" autocomplete="given-name"
                     :value="hub['model_number']"
                     readonly
                     class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
            </div>
            <div class="col-span-6 sm:col-span-3">
              <label for="model-number" class="block text-sm font-medium text-gray-700">MAC Address</label>
              <input type="text" name="model-number" id="model-number" autocomplete="given-name"
                     :value="hub['mac_address']"
                     readonly
                     class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
            </div>
            <div v-if="permissions.isSonicu()"
                 class="col-span-6 sm:col-span-3">
              <label for="model-number" class="block text-sm font-medium text-gray-700">Manufacturer Serial</label>
              <input type="text" name="model-number" id="model-number" autocomplete="given-name"
                     :value="hub['manufacturer_serial']"
                     readonly
                     class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
            </div>
            <div class="col-span-6 sm:col-span-3">
              <label for="last-connected" class="block text-sm font-medium text-gray-700">Last Connected</label>
              <input type="text" name="last-connected" id="last-connected" autocomplete="given-name"
                     :value="hubLastCheckinData"
                     readonly
                     class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
            </div>
            <div class="col-span-6 sm:col-span-3 mb-4">
              <label for="last-connected" class="block text-sm font-medium text-gray-700">Refresh</label>
              <button-blue-primary
                @click="loadData()"
                @click.middle="loadData()"
              >
                Refresh Hub Data
              </button-blue-primary>
            </div>
            <div v-if="permissions.isSonicu() || permissions.isSonicuViewOnly()"
                 class="col-span-6 sm:col-span-3">
              <label for="model-number" class="block text-sm font-medium text-gray-700">View Nova</label>
              <button-blue-secondary
                @click="viewHubNova()"
                @click.middle="viewHubNova()"
              >
                View Hub on Nova
              </button-blue-secondary>
            </div>
            <div v-if="canSeeExtendedInfo"
                 v-for="diagnosticPoint in hubDiagnosticPoints"
                 class="col-span-6 sm:col-span-3">
              <button-blue-secondary
                @click="loadPoint(diagnosticPoint['id'])"
                @click.middle="loadPoint(diagnosticPoint['id'])"
              >
                {{diagnosticPoint['name']}}
              </button-blue-secondary>
            </div>
            <div v-if="offline" class="col-span-6 sm:col-span-3 py-4">
              <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                <strong class="font-bold">Offline!</strong>
                <span class="block sm:inline px-2">This hub is currently offline. </span>
                <div class="py-2"><a target="_blank" href="https://support.sonicu.com/sonilink-hub-troubleshooting-guide">Hub Troubleshooting Guide</a></div>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-5 md:col-span-2 md:mt-0">
          <div class="overflow-hidden bg-white shadow sm:rounded-md">
            <loading v-if="loading" width="w-full"></loading>
            <div v-if="!loading && hubMeterData.length === 0" class="px-4 py-5 sm:px-6">
              <p class="text-sm font-medium text-gray-900">No points visible or loaded</p>
            </div>
            <ul role="list" class="divide-y divide-gray-200">
              <li v-for="(point, index) in hubMeterData" :key="index">
                <div class="px-4 py-4 sm:px-6">
                  <div class="flex items-center justify-between">
                    <p class="truncate text-sm font-medium text-blue-600">{{ point.serial_number }}</p>
                    <div class="ml-2 flex flex-shrink-0">
                      <p class="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">{{ point.device_type }}</p>
                    </div>
                  </div>
                  <div class="mt-2 grid grid-cols-3 gap-4">
                    <div class="flex-col">
                      <div v-if="point.point_names.length === 0">No meters visible or connected to this Hub</div>
                      <p v-for="(names, point_id) in point.point_names"
                         @click="loadPoint(point_id)"
                         class="flex items-center text-sm text-gray-500">
                        <p class="cursor-pointer border-dotted border-b-2">{{ names }}</p>
                      </p>
                    </div>
                    <div class="mt-2 flex-col items-center text-sm text-gray-500 sm:mt-0">
                      <p v-if="canSeeExtendedInfo">
                        Firmware Version: {{ point.firmware_version }}
                      </p>
                      <p v-if="point.last_update">
                        Last Update: {{ point.last_update }}
                      </p>
                    </div>
                    <div v-if="canSeeExtendedInfo"
                      class="mt-2 flex-col items-center text-sm text-gray-500 sm:mt-0">
                      <div class="flex items-center text-sm text-gray-500">
                        <p v-if="point.signal_strength_meter_id" @click="loadPoint(point.signal_strength_meter_id)"
                           class="cursor-pointer border-dotted border-b-2">
                          Signal Strength: {{ point.signal_strength }}
                        </p>
                        <p v-else>
                          Signal Strength: {{ point.signal_strength }}
                        </p>
                      </div>
                      <div class="flex items-center text-sm text-gray-500">
                        <p v-if="point.signal_to_noise_ratio_meter_id" @click="loadPoint(point.signal_to_noise_ratio_meter_id)"
                           class="cursor-pointer border-dotted border-b-2">
                          Signal/Noise: {{ point.signal_to_noise_ratio }}
                        </p>
                        <p v-else>
                          Signal/Noise: {{ point.signal_to_noise_ratio }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary.vue";
import axios from "axios";
import ButtonBluePrimary from "@/components/elements/buttons/ButtonBluePrimary.vue";
import Loading from "@/components/elements/loading.vue";
export default {
  name: "SonilinkHubsIndex",
  components: {Loading, ButtonBluePrimary, ButtonBlueSecondary},
  mixins: [SonicuCoreMixin],
  props: {
    hub: {
      type: Object,
      required: true
    },
    canSeeExtendedInfo: {
      type: Boolean,
      required: true
    }
  },
  data(){
    return {
      'data': {},
      hubDiagnosticPoints: [],
      hubMeterData: [],
      hubLastCheckinData: 'N/A',
      loading: true,
      offline: false
    }
  },
  mounted(){
    this.loadData();
  },
  methods: {
    loadData(){
      axios.get(this.company.buildCompanyApiInternalRoute(`sonilink-hub/` + this.hub['id']), {
      }).then(response => {
        this.loading = false;
        this.data = response.data.data
        this.hubDiagnosticPoints =  this.data['diagnostic_points']
        this.hubMeterData = this.data['device_info']
        this.hubLastCheckinData = this.data['last_checkin_aws']
        this.offline = this.data['hub_offline']
      }).catch(error => {
        console.log(`Error: ${error}`)
      }).then(() => {
      })
    },
    viewHubNova() {
      let novaUrl = "/nova/resources/sonilink-hubs/" + this.hub['id'];
        window.open(novaUrl, '_blank')
    },
    loadPoint(id) {
      let targetUrl = this.company.buildCompanyRoute("meters/" + id);
        window.open(targetUrl, '_blank')
    },
  }
}
</script>

<style scoped>

</style>